import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Item colours (action-view, action-deactivate) shared with the other Actions menus.
import '../pages/customers/LoanCustomers.css';

export interface ActionsMenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  /** Shown under a disabled item, to say why it cannot be used. */
  hint?: string;
  /** 'danger' for destructive actions such as delete. */
  variant?: 'default' | 'danger';
  dividerBefore?: boolean;
}

interface ActionsMenuProps {
  items: ActionsMenuItem[];
  label?: string;
  id?: string;
}

const MENU_WIDTH = 240;
const GAP = 4;

/**
 * The row "Actions" menu, drawn on top of the whole page.
 *
 * Unlike a Bootstrap dropdown it is rendered into document.body with fixed positioning, so a
 * scrolling table cannot clip it, and it opens upwards when there is no room below. It also avoids
 * the `dropdown-menu` class on purpose: Bootstrap's own script (loaded globally) handles key
 * presses on any open `.dropdown-menu`, cannot find a toggle for a React one, and throws
 * "Cannot read properties of undefined (reading 'parentNode')".
 */
const ActionsMenu: React.FC<ActionsMenuProps> = ({ items, label = 'Actions', id }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const toggle = toggleRef.current;
    if (!toggle) return;
    const rect = toggle.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 44 * items.length + 16;
    const fitsBelow = rect.bottom + GAP + menuHeight <= window.innerHeight;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setPosition(fitsBelow
      ? { top: rect.bottom + GAP, left }
      : { top: Math.max(8, rect.top - GAP - menuHeight), left });
  }, [items.length]);

  // Measured after the menu is in the page, so the flip-upwards check uses its real height.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !toggleRef.current?.contains(target)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        toggleRef.current?.focus();
      }
    };
    // Any scroll (the page or the table) would leave a fixed menu floating away from its row.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={toggleRef}
        id={id}
        type="button"
        className="btn btn-light btn-sm rounded-pill d-inline-flex align-items-center"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span>{label}</span>
        <i className="fa fa-caret-down ms-2"></i>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-labelledby={id}
          className="actions-menu bg-white border rounded-3 shadow py-2"
          style={{ position: 'fixed', zIndex: 2000, width: MENU_WIDTH, ...position }}
        >
          {items.map((item, index) => (
            <React.Fragment key={`${item.label}-${index}`}>
              {item.dividerBefore && <div className="dropdown-divider" role="separator" />}
              <button
                type="button"
                role="menuitem"
                className={`dropdown-item d-block text-start ${item.variant === 'danger' ? 'action-deactivate' : 'action-view'}`}
                style={{ whiteSpace: 'normal' }}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
              >
                {item.icon && <i className={`fa ${item.icon} me-2`}></i>}
                {item.label}
                {item.disabled && item.hint && <div className="small text-muted">{item.hint}</div>}
              </button>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionsMenu;
